// src/pages/MRPInventoryTab.jsx
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import * as XLSX from "xlsx";
import UserAccessSummary from "../components/layout/UserAccessSummary";
import { getCurrentUser } from "../utils/auth";
import { permissionLevelRank, usePermissions } from "../utils/permissions";
import { filterRowsByAccess, getUserAccessInfo, NULL_ACCESS_TOKEN } from "../utils/accessFilters";
import useAccessOverrideVersion from "../hooks/useAccessOverrideVersion";
import { buildInventoryPivotQuery, INVENTORY_PIVOT_EMPTY_TOKEN } from "../utils/mrpInventoryPivot";

const API_BASE = process.env.REACT_APP_API_URL || "https://api.portpilot.co";
const REFRESH_JOB_STORAGE_KEY = "mrp.inventory.refreshJobId";
const SNAPSHOT_LIST_ENDPOINT = "/api/mrp/inventory/snapshots";
const SNAPSHOT_BY_DATE_ENDPOINT = "/api/mrp/inventory/snapshot";
const SNAPSHOT_REFRESH_ASYNC_ENDPOINT = "/api/mrp/inventory/snapshot/refresh-async";
const SNAPSHOT_REFRESH_STATUS_ENDPOINT = "/api/mrp/inventory/snapshot/refresh-status";

const COLUMN_DEFINITIONS = [
  { key: "custCode", label: "Cust Code", width: 140 },
  { key: "retailSku", label: "Retail SKU", width: 160 },
  { key: "productCode", label: "Product Code", width: 160 },
  { key: "itemName", label: "Item Name", width: 240 },
  { key: "warehouse", label: "Warehouse", width: 140 },
  { key: "ttlWhs", label: "Ttl WHS", width: 140 },
  { key: "whsOb", label: "WHS OB", width: 140 },
  { key: "whsQc", label: "WHS QC", width: 140 },
  { key: "whsAvail", label: "WHS Avail", width: 140 },
  { key: "whsReturns", label: "WHS Returns", width: 150 },
  { key: "wmsPending", label: "WMS Pending", width: 150 },
  { key: "netAvail", label: "Net Avail", width: 140 },
  { key: "inTransitAssigned", label: "In-Transit(A)", width: 150 },
  { key: "inTransitUnassigned", label: "In-Transit(U)", width: 150 },
  { key: "planning", label: "Planning", width: 150 },
];

const IN_TRANSIT_TOOLTIPS = {
  inTransitAssigned: "Warehouse Assigned",
  inTransitUnassigned: "Warehouse Unassigned",
};

const RIGHT_ALIGN_COLUMNS = new Set([
  "ttlWhs",
  "whsOb",
  "whsQc",
  "whsAvail",
  "whsReturns",
  "wmsPending",
  "netAvail",
  "inTransitAssigned",
  "inTransitUnassigned",
  "planning",
]);

const DEFAULT_COLUMN_WIDTHS = COLUMN_DEFINITIONS.reduce((acc, column) => {
  acc[column.key] = column.width || 180;
  return acc;
}, {});

const DEFAULT_PAGE_OPTIONS = [25, 50, 100, "All"];
const MIN_TABLE_ROWS = 20;
const TABLE_ROW_HEIGHT_PX = 42;
const MIN_TABLE_HEIGHT = MIN_TABLE_ROWS * TABLE_ROW_HEIGHT_PX;
const EMPTY_VALUE = "__EMPTY__";

const toArray = (value) => (Array.isArray(value) ? value : value ? [value] : []);
const arraysEqual = (a = [], b = []) => a.length === b.length && a.every((val, idx) => val === b[idx]);
const getDefaultWidth = (key) => DEFAULT_COLUMN_WIDTHS[key] || 180;

const normaliseFilterValue = (value) => {
  if (value === null || value === undefined) return EMPTY_VALUE;
  if (typeof value === "number") {
    if (Number.isNaN(value)) return EMPTY_VALUE;
    return String(value);
  }
  if (typeof value === "boolean") {
    return value ? "true" : "false";
  }
  const trimmed = String(value).trim();
  return trimmed === "" ? EMPTY_VALUE : trimmed;
};

const displayLabelForToken = (raw, token) => {
  if (token === EMPTY_VALUE) return "(Blank)";
  if (raw === null || raw === undefined) return String(token);
  if (typeof raw === "boolean") return raw ? "On" : "Hold";
  return String(raw);
};

const formatTimestampForFilename = (date) => {
  const pad = (n) => String(n).padStart(2, "0");
  return `${date.getFullYear()}${pad(date.getMonth() + 1)}${pad(date.getDate())}-${pad(date.getHours())}${pad(date.getMinutes())}${pad(date.getSeconds())}`;
};

const formatUtcTimestamp = (value) => {
  if (!value) return "-";
  let iso = String(value);
  const hasTimezone = /([zZ]|[+-]\d{2}:?\d{2})$/.test(iso);
  if (!hasTimezone) {
    if (!iso.includes("T")) {
      iso = iso.replace(" ", "T");
    }
    iso = `${iso}Z`;
  }
  const dt = new Date(iso);
  if (Number.isNaN(dt.getTime())) return String(value);
  const pad = (n) => String(n).padStart(2, "0");
  return `${dt.getUTCFullYear()}-${pad(dt.getUTCMonth() + 1)}-${pad(dt.getUTCDate())} ${pad(dt.getUTCHours())}:${pad(
    dt.getUTCMinutes()
  )}:${pad(dt.getUTCSeconds())} UTC`;
};

const numberLike = (value) => {
  if (value === null || value === undefined || value === "") return null;
  if (typeof value === "number") return value;
  const parsed = Number(String(value).replace(/,/g, "").trim());
  return Number.isFinite(parsed) ? parsed : null;
};

const compareForSort = (left, right) => {
  const leftNumber = numberLike(left);
  const rightNumber = numberLike(right);
  if (leftNumber !== null && rightNumber !== null) {
    return leftNumber - rightNumber;
  }
  const leftString = left === null || left === undefined ? "" : String(left).toLowerCase();
  const rightString = right === null || right === undefined ? "" : String(right).toLowerCase();
  return leftString.localeCompare(rightString);
};

const createInitialFilters = () => {
  const next = {};
  COLUMN_DEFINITIONS.forEach(({ key }) => {
    next[key] = [];
  });
  return next;
};

const createInitialWidths = () =>
  COLUMN_DEFINITIONS.reduce((acc, column) => {
    acc[column.key] = getDefaultWidth(column.key);
    return acc;
  }, {});

const buildRowsFromPayload = (payload) => {
  if (!payload) return [];
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload.rows)) return payload.rows;
  if (Array.isArray(payload.data)) return payload.data;
  return [];
};

export default function MRPInventoryTab() {
  const navigate = useNavigate();
  const [rawRows, setRawRows] = useState([]);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [sortKey, setSortKey] = useState(COLUMN_DEFINITIONS[0]?.key || "");
  const [sortDirection, setSortDirection] = useState(0);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [filters, setFilters] = useState(() => createInitialFilters());
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_OPTIONS[0]);
  const [page, setPage] = useState(1);
  const [visibleColumns, setVisibleColumns] = useState(() => COLUMN_DEFINITIONS.map(({ key }) => key));
  const [widths, setWidths] = useState(() => createInitialWidths());
  const [refreshingSnapshot, setRefreshingSnapshot] = useState(false);
  const [refreshJobId, setRefreshJobId] = useState("");
  const [refreshJobStatus, setRefreshJobStatus] = useState("");
  const [refreshJobError, setRefreshJobError] = useState("");
  const [refreshSummary, setRefreshSummary] = useState(null);
  const [snapshotMeta, setSnapshotMeta] = useState(null);
  const columnOptions = useMemo(() => COLUMN_DEFINITIONS.map(({ key, label }) => ({ value: key, label })), []);
  const dragRef = useRef({ key: "", startX: 0, startW: 0 });
  const me = getCurrentUser();
  const { getLevel } = usePermissions();
  const permissionLevel = getLevel(me?.role, "mrp-inventory");
  const canRefreshSnapshot = permissionLevelRank(permissionLevel) >= permissionLevelRank("Read");
  const buildAuthHeaders = useCallback(() => {
    const headers = {};
    if (me?.email) headers["X-User-Email"] = me.email;
    if (me?.role) headers["X-User-Role"] = me.role || "";
    return headers;
  }, [me?.email, me?.role]);
  const accessVersion = useAccessOverrideVersion();
  const accessInfo = useMemo(() => {
    void accessVersion;
    return getUserAccessInfo();
  }, [accessVersion]);
  const {
    activeCustTokens,
    activeWhsTokens,
    restrictCust,
    restrictWhs,
  } = accessInfo;

  const applyAccessFilters = useCallback(
    (dataRows = []) =>
      filterRowsByAccess(dataRows, {
        columns: COLUMN_DEFINITIONS.map((column) => column.key),
        custKeys: ["custCode"],
        whsKeys: ["warehouse"],
        allowBlank: false,
      }),
    []
  );

  const syncAccessRows = useCallback(
    (dataRows) => {
      setRows(applyAccessFilters(dataRows || []));
    },
    [applyAccessFilters]
  );

  useEffect(() => {
    syncAccessRows(rawRows);
  }, [rawRows, accessVersion, syncAccessRows]);

  const fetchSnapshotByDate = useCallback(
    async (date, headers) => {
      if (!date) return null;
      const resp = await fetch(
        `${API_BASE}${SNAPSHOT_BY_DATE_ENDPOINT}?date=${encodeURIComponent(date)}`,
        { headers }
      );
      const payload = await resp.json().catch(() => ({}));
      if (!resp.ok || payload?.ok !== true) return null;
      const rows = buildRowsFromPayload(payload);
      if (!rows.length) return null;
      return { rows, generatedAt: payload.generatedAt, date };
    },
    []
  );

  const fetchLatestSnapshot = useCallback(
    async (headers) => {
      const resp = await fetch(`${API_BASE}${SNAPSHOT_LIST_ENDPOINT}`, { headers });
      const payload = await resp.json().catch(() => ({}));
      if (!resp.ok || payload?.ok !== true) return null;
      const snapshots = Array.isArray(payload.snapshots) ? payload.snapshots : [];
      if (!snapshots.length) return null;
      const latest = snapshots
        .map((snap) => snap?.snapshot_date)
        .filter(Boolean)
        .sort()
        .slice(-1)[0];
      if (!latest) return null;
      return fetchSnapshotByDate(latest, headers);
    },
    [fetchSnapshotByDate]
  );

  const loadLatestSnapshot = useCallback(
    async (headers) => fetchLatestSnapshot(headers),
    [fetchLatestSnapshot]
  );

  const fetchRows = useCallback(async () => {
    if (permissionLevel === "None") {
      setRawRows([]);
      syncAccessRows([]);
      setLoading(false);
      setError("");
      setLastUpdated(null);
      return;
    }
    setLoading(true);
    setError("");
    try {
      const headers = buildAuthHeaders();
      const latest = await loadLatestSnapshot(headers);
      if (!latest?.rows?.length) {
        throw new Error("No inventory snapshots available.");
      }
      setRawRows(latest.rows);
      syncAccessRows(latest.rows);
      setLastUpdated(latest.generatedAt ? new Date(latest.generatedAt) : new Date());
      setSnapshotMeta({ date: latest.date, generatedAt: latest.generatedAt });
      setRefreshSummary(null);
      setPage(1);
    } catch (err) {
      setRawRows([]);
      syncAccessRows([]);
      setError(err.message || "Failed to load MRP Inventory data.");
      setLastUpdated(null);
      setSnapshotMeta(null);
    } finally {
      setLoading(false);
    }
  }, [permissionLevel, buildAuthHeaders, loadLatestSnapshot, syncAccessRows]);

  const refreshSnapshot = useCallback(async () => {
    if (refreshingSnapshot || refreshJobStatus === "running") return;
    if (!canRefreshSnapshot) return;
    const confirmed = window.confirm(
      "This will pull live data and generate a new snapshot. Continue?"
    );
    if (!confirmed) return;
    setRefreshingSnapshot(true);
    setError("");
    setRefreshJobError("");
    setRefreshSummary(null);
    try {
      const headers = buildAuthHeaders();
      const response = await fetch(`${API_BASE}${SNAPSHOT_REFRESH_ASYNC_ENDPOINT}`, {
        method: "POST",
        headers,
      });
      const payload = await response.json().catch(() => ({}));
      if (!response.ok || payload?.ok !== true) {
        throw new Error(payload?.error || "Failed to start snapshot refresh.");
      }
      setRefreshJobId(payload?.jobId || "");
      setRefreshJobStatus(payload?.status || "running");
      try {
        if (payload?.jobId) {
          localStorage.setItem(REFRESH_JOB_STORAGE_KEY, payload.jobId);
        }
      } catch {
        /* ignore */
      }
    } catch (err) {
      setError(err.message || "Failed to start snapshot refresh.");
      setRefreshJobError("Snapshot refresh failed. Please retry. (See server logs)");
      try {
        localStorage.removeItem(REFRESH_JOB_STORAGE_KEY);
      } catch {
        /* ignore */
      }
      try {
        window.alert(err.message || "Failed to start snapshot refresh.");
      } catch {
        /* noop */
      }
      setRefreshingSnapshot(false);
    }
  }, [refreshingSnapshot, refreshJobStatus, canRefreshSnapshot, buildAuthHeaders]);

  useEffect(() => {
    if (!canRefreshSnapshot) return;
    if (!refreshJobId || refreshJobStatus !== "running") return;
    let cancelled = false;
    const interval = setInterval(async () => {
      try {
        const headers = buildAuthHeaders();
        const resp = await fetch(
          `${API_BASE}${SNAPSHOT_REFRESH_STATUS_ENDPOINT}/${encodeURIComponent(refreshJobId)}`,
          { headers }
        );
        const payload = await resp.json().catch(() => ({}));
        if (!resp.ok || payload?.ok !== true) {
          throw new Error(payload?.error || "Failed to fetch refresh status.");
        }
        if (payload.status === "running") return;
        if (cancelled) return;
        if (payload.status === "failed") {
          setRefreshJobStatus("failed");
          setRefreshingSnapshot(false);
          setRefreshJobError("Snapshot refresh failed. Please retry. (See server logs)");
          try {
            localStorage.removeItem(REFRESH_JOB_STORAGE_KEY);
          } catch {
            /* ignore */
          }
          return;
        }
        setRefreshJobStatus("done");
        setRefreshingSnapshot(false);
        setRefreshSummary(payload?.summary || null);
        try {
          localStorage.removeItem(REFRESH_JOB_STORAGE_KEY);
        } catch {
          /* ignore */
        }
        const snapshotDate = payload?.snapshotDate;
        const refreshed = snapshotDate
          ? await fetchSnapshotByDate(snapshotDate, headers)
          : await loadLatestSnapshot(headers);
        if (refreshed?.rows?.length) {
          setRawRows(refreshed.rows);
          syncAccessRows(refreshed.rows);
          setLastUpdated(refreshed.generatedAt ? new Date(refreshed.generatedAt) : new Date());
          setSnapshotMeta({ date: refreshed.date, generatedAt: refreshed.generatedAt });
          setPage(1);
        }
      } catch (err) {
        if (cancelled) return;
        setRefreshJobStatus("failed");
        setRefreshingSnapshot(false);
        setRefreshJobError("Snapshot refresh failed. Please retry. (See server logs)");
        try {
          localStorage.removeItem(REFRESH_JOB_STORAGE_KEY);
        } catch {
          /* ignore */
        }
      }
    }, 2000);
    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, [canRefreshSnapshot, refreshJobId, refreshJobStatus, buildAuthHeaders, fetchSnapshotByDate, loadLatestSnapshot, syncAccessRows]);

  useEffect(() => {
    fetchRows();
  }, [fetchRows]);

  useEffect(() => {
    if (!canRefreshSnapshot) return;
    try {
      const storedJobId = localStorage.getItem(REFRESH_JOB_STORAGE_KEY) || "";
      if (storedJobId && !refreshJobId) {
        setRefreshJobId(storedJobId);
        setRefreshJobStatus("running");
      }
    } catch {
      /* ignore */
    }
  }, [canRefreshSnapshot, refreshJobId]);

  useEffect(() => {
    setPage(1);
  }, [searchTerm]);

  const derivedRows = useMemo(() => rows, [rows]);

  const filterOptions = useMemo(() => {
    const map = {};
    const seen = {};
    COLUMN_DEFINITIONS.forEach(({ key }) => {
      map[key] = [];
      seen[key] = new Set();
    });
    derivedRows.forEach((row = {}) => {
      COLUMN_DEFINITIONS.forEach(({ key }) => {
        const raw = row?.[key];
        const token = normaliseFilterValue(raw);
        if (seen[key].has(token)) return;
        seen[key].add(token);
        map[key].push({ value: token, label: displayLabelForToken(raw, token) });
      });
    });
    Object.keys(map).forEach((key) => {
      map[key].sort((a, b) => a.label.localeCompare(b.label));
    });
    return map;
  }, [derivedRows]);

  const displayColumns = useMemo(
    () => COLUMN_DEFINITIONS.filter(({ key }) => visibleColumns.includes(key)),
    [visibleColumns]
  );

  const onVisibleColumnsChange = useCallback(
    (next) => {
      const list = toArray(next);
      if (!list.length) return;
      const ordered = COLUMN_DEFINITIONS.map(({ key }) => key).filter((key) => list.includes(key));
      setVisibleColumns(ordered);
      if (sortKey && !ordered.includes(sortKey)) {
        setSortKey("");
        setSortDirection(0);
      }
    },
    [sortKey]
  );

  const onDragging = useCallback((event) => {
    const { key, startX, startW } = dragRef.current;
    if (!key) return;
    const dx = event.clientX - startX;
    const nextWidth = Math.max(80, startW + dx);
    setWidths((prev) => {
      if (prev[key] === nextWidth) return prev;
      return { ...prev, [key]: nextWidth };
    });
  }, []);

  const onDragEnd = useCallback(() => {
    window.removeEventListener("mousemove", onDragging);
    window.removeEventListener("mouseup", onDragEnd);
    dragRef.current = { key: "", startX: 0, startW: 0 };
  }, [onDragging]);

  const onDragStart = (event, key) => {
    dragRef.current = { key, startX: event.clientX, startW: widths[key] || getDefaultWidth(key) };
    window.addEventListener("mousemove", onDragging);
    window.addEventListener("mouseup", onDragEnd);
    event.preventDefault();
  };

  useEffect(() => {
    return () => {
      window.removeEventListener("mousemove", onDragging);
      window.removeEventListener("mouseup", onDragEnd);
    };
  }, [onDragging, onDragEnd]);

  const setFilter = useCallback((key, value) => {
    setFilters((prev) => {
      const current = prev[key];
      if (Array.isArray(value)) {
        const currentArray = Array.isArray(current) ? current : [];
        if (arraysEqual(currentArray, value)) return prev;
        return { ...prev, [key]: value };
      }
      if (current === value) return prev;
      return { ...prev, [key]: value };
    });
    setPage(1);
  }, []);

  const resetFilters = useCallback(() => {
    setFilters(createInitialFilters());
    setPage(1);
  }, [setPage]);

  const filteredRows = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    const activeFilters = COLUMN_DEFINITIONS.filter(({ key }) => (filters[key] || []).length > 0);
    return derivedRows.filter((row) => {
      for (const { key } of activeFilters) {
        const selected = filters[key] || [];
        if (!selected.length) continue;
        const token = normaliseFilterValue(row?.[key]);
        if (!selected.includes(token)) return false;
      }
      if (!term) return true;
      return COLUMN_DEFINITIONS.some(({ key }) => {
        const value = row?.[key];
        if (value === null || value === undefined) return false;
        return String(value).toLowerCase().includes(term);
      });
    });
  }, [derivedRows, filters, searchTerm]);

  const sortedRows = useMemo(() => {
    if (!sortKey || sortDirection === 0) return filteredRows;
    const next = [...filteredRows];
    next.sort((a, b) => {
      const left = a?.[sortKey];
      const right = b?.[sortKey];
      const base = compareForSort(left, right);
      return sortDirection === -1 ? -base : base;
    });
    return next;
  }, [filteredRows, sortKey, sortDirection]);

  const totalRows = filteredRows.length;
  const totalPages = useMemo(() => {
    if (!totalRows) return 1;
    const size = pageSize === "All" ? totalRows || 1 : pageSize;
    return Math.max(1, Math.ceil(totalRows / size));
  }, [totalRows, pageSize]);

  useEffect(() => {
    if (page > totalPages) setPage(totalPages);
  }, [page, totalPages]);

  const paginatedRows = useMemo(() => {
    if (pageSize === "All") return sortedRows;
    const start = (page - 1) * pageSize;
    return sortedRows.slice(start, start + pageSize);
  }, [sortedRows, page, pageSize]);

  const startIdx = totalRows === 0 ? 0 : pageSize === "All" ? 0 : (page - 1) * pageSize;
  const endIdx =
    totalRows === 0
      ? 0
      : pageSize === "All"
      ? totalRows
      : Math.min(totalRows, startIdx + (typeof pageSize === "number" ? pageSize : totalRows));

  const toggleSort = (key) => {
    if (sortKey !== key || !sortKey) {
      setSortKey(key);
      setSortDirection(1);
      return;
    }
    const nextDirection = sortDirection === 1 ? -1 : sortDirection === -1 ? 0 : 1;
    if (nextDirection === 0) {
      setSortDirection(0);
      setSortKey("");
    } else {
      setSortDirection(nextDirection);
    }
  };

  const sortIcon = (key) => {
    if (sortKey !== key || sortDirection === 0) {
      return (
        <svg className="h-3.5 w-3.5 opacity-40" viewBox="0 0 20 20" fill="currentColor">
          <path d="M7 7l3-3 3 3H7zM13 13l-3 3-3-3h6z" />
        </svg>
      );
    }
    if (sortDirection === 1) {
      return (
        <svg className="h-3.5 w-3.5" viewBox="0 0 20 20" fill="currentColor">
          <path d="M7 11l3 3 3-3H7z" />
        </svg>
      );
    }
    return (
      <svg className="h-3.5 w-3.5" viewBox="0 0 20 20" fill="currentColor">
        <path d="M7 9l3-3 3 3H7z" />
      </svg>
    );
  };

  const onExport = useCallback(() => {
    if (!sortedRows.length) return;
    const header = COLUMN_DEFINITIONS.map((column) => column.label);
    const dataRows = sortedRows.map((row) =>
      COLUMN_DEFINITIONS.map((column) => {
        const value = row?.[column.key];
        return value === null || value === undefined ? "" : value;
      })
    );
    const worksheet = XLSX.utils.aoa_to_sheet([header, ...dataRows]);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "MRP Inventory");
    const filename = `MRP_Inventory_${formatTimestampForFilename(new Date())}.xlsx`;
    XLSX.writeFile(workbook, filename);
  }, [sortedRows]);

  const exportDisabled = sortedRows.length === 0;
  const pivotDisabled = filteredRows.length === 0;
  const pivotFilters = useMemo(() => {
    const merged = {};
    Object.keys(filters || {}).forEach((key) => {
      merged[key] = Array.isArray(filters[key]) ? [...filters[key]] : filters[key];
    });
    const mapAccessTokens = (tokens = []) =>
      tokens
        .filter((token) => token !== undefined && token !== null)
        .map((token) => (token === NULL_ACCESS_TOKEN ? INVENTORY_PIVOT_EMPTY_TOKEN : token));
    if ((!merged.custCode || merged.custCode.length === 0) && restrictCust && activeCustTokens.length) {
      merged.custCode = mapAccessTokens(activeCustTokens);
    }
    if ((!merged.warehouse || merged.warehouse.length === 0) && restrictWhs && activeWhsTokens.length) {
      merged.warehouse = mapAccessTokens(activeWhsTokens);
    }
    return merged;
  }, [filters, restrictCust, restrictWhs, activeCustTokens, activeWhsTokens]);

  const pivotQuery = useMemo(() => buildInventoryPivotQuery(pivotFilters), [pivotFilters]);

  const onPivot = useCallback(() => {
    const query = pivotQuery || "";
    navigate(`/mrp/inventory/pivot${query}`);
  }, [navigate, pivotQuery]);

  if (!COLUMN_DEFINITIONS.length) {
    return (
      <div className="rounded border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-700">
        Populate COLUMN_DEFINITIONS before rendering MRPInventoryTab.
      </div>
    );
  }

  if (permissionLevel === "None") {
    return (
      <div className="space-y-4">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <h1 className="text-2xl font-semibold text-slate-900">MRP Inventory</h1>
          <UserAccessSummary />
        </div>
        <div className="rounded border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-700">
          You do not have access to MRP Inventory.
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="space-y-3">
        <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="flex flex-col gap-2">
          <div className="flex flex-wrap items-center gap-4">
            <h1 className="text-2xl font-semibold text-slate-900">MRP Inventory</h1>
            <span className="text-sm text-slate-600 whitespace-nowrap">
              Filtered {totalRows.toLocaleString()} rows
            </span>
            <div className="flex flex-wrap items-center gap-2 text-sm text-slate-600">
              <input
                type="text"
                value={searchTerm}
                onChange={(event) => setSearchTerm(event.target.value)}
                placeholder="Search all columns..."
                className="w-64 rounded border border-slate-300 px-3 py-2 text-sm shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
              />
              <button
                type="button"
                onClick={onExport}
                disabled={exportDisabled}
                className="inline-flex items-center gap-2 rounded border border-slate-300 bg-white px-3 py-2 text-sm font-medium text-slate-700 shadow-sm hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1 disabled:cursor-not-allowed disabled:opacity-40"
              >
                <svg className="h-4 w-4 text-slate-500" viewBox="0 0 20 20" fill="currentColor">
                  <path d="M13 7V3H7v4H4l6 6 6-6h-3z" />
                  <path d="M4 15h12v2H4z" />
                </svg>
                Export
              </button>
              <button
                type="button"
                onClick={onPivot}
                disabled={pivotDisabled}
                className="inline-flex items-center gap-2 rounded border border-slate-300 bg-white px-3 py-2 text-sm font-medium text-slate-700 shadow-sm hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1 disabled:cursor-not-allowed disabled:opacity-40"
              >
                <svg className="h-4 w-4 text-slate-500" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M4 4h12v4H4z" />
                  <path d="M4 12h5v4H4zM11 12h5v4h-5z" />
                </svg>
                Pivot
              </button>
              {canRefreshSnapshot && (
                <>
                  <button
                    type="button"
                    onClick={refreshSnapshot}
                    className="inline-flex items-center gap-2 rounded border border-slate-300 bg-white px-3 py-2 text-sm font-medium text-slate-700 shadow-sm hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1 disabled:opacity-40"
                    disabled={loading || refreshingSnapshot || refreshJobStatus === "running"}
                    title="Long refresh may take 1–3 minutes depending on iLS latency."
                  >
                    <svg
                      className={`h-4 w-4 ${refreshingSnapshot ? "animate-spin text-blue-500" : "text-slate-500"}`}
                      viewBox="0 0 20 20"
                      fill="currentColor"
                    >
                      <path
                        fillRule="evenodd"
                        d="M4 10a6 6 0 0110.39-4.19l1.42-1.42A8 8 0 102 10h2zm12-4h-4v2h4V6z"
                        clipRule="evenodd"
                      />
                    </svg>
                    Refresh Snapshot
                  </button>
                </>
              )}
            </div>
          </div>
          <div className="text-sm text-slate-500">
            {lastUpdated ? `Last updated ${lastUpdated.toLocaleString()}` : loading ? "Loading..." : "Awaiting data"}
          </div>
        </div>
        <UserAccessSummary />
      </div>

      {snapshotMeta?.date && (
        <div className="rounded border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-700 flex flex-wrap items-center justify-between gap-3">
          <div>
            <div>
              Showing snapshot: {snapshotMeta.date}
              {snapshotMeta.generatedAt ? ` (generated at ${formatUtcTimestamp(snapshotMeta.generatedAt)})` : ""}
            </div>
            {refreshSummary && (
              <div className="text-sm text-slate-600">
                {`Processed = ${refreshSummary.processed ?? 0}, Skipped(time_budget) = ${
                  refreshSummary.skipped_time_budget ?? 0
                }, Skipped(iLS Hold in Product) = ${
                  refreshSummary.skipped_update_disabled ?? 0
                }, Total = ${refreshSummary.total ?? 0}`}
              </div>
            )}
          </div>
        </div>
      )}
      {refreshJobStatus === "running" && (
        <div className="rounded border border-blue-200 bg-blue-50 px-4 py-3 text-sm text-blue-700">
          Refreshing snapshot...
        </div>
      )}
      {refreshJobError && (
        <div className="rounded border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
          {refreshJobError}
        </div>
      )}

      <div className="flex flex-wrap items-center justify-end gap-2 text-sm text-slate-600">
        <button
          type="button"
          onClick={resetFilters}
          title="Reset filters"
          aria-label="Reset filters"
          className="inline-flex h-8 w-8 items-center justify-center rounded-full bg-green-600 text-white hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-1"
        >
          <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M21 12a9 9 0 1 1-3-6.7L21 7" />
            <path d="M21 3v4h-4" />
          </svg>
        </button>
        <MultiSelectDropdown
          value={visibleColumns}
          options={columnOptions}
          onChange={onVisibleColumnsChange}
          placeholder="Columns"
          buttonClassName="text-xs min-w-[150px]"
        />
        <span>Rows:</span>
        <select
          className="border rounded px-2 py-1 text-sm"
          value={pageSize}
          onChange={(event) => {
            const value = event.target.value;
            setPageSize(value === "All" ? "All" : parseInt(value, 10));
            setPage(1);
          }}
        >
          {DEFAULT_PAGE_OPTIONS.map((option) => (
            <option key={option} value={option}>
              {option}
            </option>
          ))}
        </select>
        <button
          type="button"
          className="rounded border border-slate-300 px-2 py-1 text-sm shadow-sm disabled:cursor-not-allowed disabled:opacity-40"
          onClick={() => setPage(1)}
          disabled={page <= 1}
          aria-label="First page"
        >
          {"<<"}
        </button>
        <button
          type="button"
          className="rounded border border-slate-300 px-2 py-1 text-sm shadow-sm disabled:cursor-not-allowed disabled:opacity-40"
          onClick={() => setPage((prev) => Math.max(1, prev - 1))}
          disabled={page <= 1}
          aria-label="Previous page"
        >
          {"<"}
        </button>
        <span className="tabular-nums">{page} / {totalPages}</span>
        <button
          type="button"
          className="rounded border border-slate-300 px-2 py-1 text-sm shadow-sm disabled:cursor-not-allowed disabled:opacity-40"
          onClick={() => setPage((prev) => Math.min(totalPages, prev + 1))}
          disabled={page >= totalPages}
          aria-label="Next page"
        >
          {">"}
        </button>
        <button
          type="button"
          className="rounded border border-slate-300 px-2 py-1 text-sm shadow-sm disabled:cursor-not-allowed disabled:opacity-40"
          onClick={() => setPage(totalPages)}
          disabled={page >= totalPages}
          aria-label="Last page"
        >
          {">>"}
        </button>
        <span className="text-slate-500 tabular-nums">
          {totalRows ? `${startIdx + 1} - ${endIdx} of ${totalRows}` : "0"}
        </span>
      </div>

      {error && (
        <div className="rounded border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">{error}</div>
      )}

      <div
        className="relative max-h-[calc(100vh-200px)] overflow-auto rounded border border-slate-200 bg-white shadow-sm"
        style={{ minHeight: MIN_TABLE_HEIGHT }}
      >
        <table className="min-w-full border-collapse">
          <thead>
            <tr>
              {displayColumns.map((column) => {
                const tooltip = IN_TRANSIT_TOOLTIPS[column.key];
                const labelContent = tooltip ? <span title={tooltip}>{column.label}</span> : column.label;
                return (
                  <th
                    key={column.key}
                    style={{ width: widths[column.key], minWidth: widths[column.key] }}
                    className={`sticky top-0 z-20 bg-white border-b px-3 py-2 text-sm font-semibold relative select-none ${
                      RIGHT_ALIGN_COLUMNS.has(column.key) ? "text-right" : "text-left"
                    }`}
                  >
                    <button
                      type="button"
                      onClick={() => toggleSort(column.key)}
                      className={`flex items-center gap-1 pr-3 ${
                        RIGHT_ALIGN_COLUMNS.has(column.key) ? "justify-end" : "text-left"
                      }`}
                    >
                      {labelContent}
                      {sortIcon(column.key)}
                    </button>
                    <span
                      onMouseDown={(event) => onDragStart(event, column.key)}
                      className="absolute right-0 top-0 h-full w-1 cursor-col-resize"
                      title="Drag to resize"
                      style={{ userSelect: "none" }}
                    />
                  </th>
                );
              })}
            </tr>
            <tr className="sticky top-[42px] z-10 bg-white">
              {displayColumns.map((column, index) => {
                const options = filterOptions[column.key] || [];
                const control = (
                  <MultiSelectDropdown
                    value={filters[column.key]}
                    options={options}
                    onChange={(next) => setFilter(column.key, next)}
                    placeholder="All"
                    buttonClassName="text-xs"
                    showSelectAll
                    showSearch
                  />
                );
                return (
                  <th
                    key={column.key}
                    className={`bg-emerald-50 border-b px-2 py-1 ${
                      RIGHT_ALIGN_COLUMNS.has(column.key) ? "text-right" : "text-left"
                    }`}
                    style={{ width: widths[column.key], minWidth: widths[column.key] }}
                  >
                    {control}
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {loading && derivedRows.length === 0 ? (
            <tr>
              <td
                  colSpan={displayColumns.length}
                  className="px-3 py-6 text-center text-sm text-slate-500"
                >
                  Loading data...
                </td>
              </tr>
            ) : paginatedRows.length === 0 ? (
              <tr>
                <td
                  colSpan={displayColumns.length}
                  className="px-3 py-6 text-center text-sm text-slate-500"
                >
                  No records match the current filters.
                </td>
              </tr>
            ) : (
              paginatedRows.map((row, rowIndex) => (
                <tr key={rowIndex} className="border-b">
                  {displayColumns.map((column) => {
                    const value = row?.[column.key];
                    const displayValue =
                      value === null || value === undefined || value === ""
                        ? "-"
                        : value;
                    const alignmentClass = RIGHT_ALIGN_COLUMNS.has(column.key)
                      ? "text-right tabular-nums"
                      : "text-left";
                    return (
                      <td
                        key={column.key}
                        className={`px-3 py-2 text-sm text-slate-700 ${alignmentClass}`}
                        style={{ width: widths[column.key], minWidth: widths[column.key] }}
                      >
                        {displayValue}
                      </td>
                    );
                  })}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
      </div>
    </>
  );
}

function MultiSelectDropdown({
  value = [],
  options = [],
  onChange,
  placeholder = "All",
  buttonClassName = "",
  showSelectAll = false,
  showSearch = false,
}) {
  const containerRef = useRef(null);
  const buttonRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [menuWidth, setMenuWidth] = useState(0);
  const [searchTerm, setSearchTerm] = useState("");
  const selectedValues = toArray(value);
  const labelMap = useMemo(() => {
    const map = new Map();
    options.forEach((option) => {
      if (!option) return;
      const val = option.value;
      if (val === undefined || val === null) return;
      map.set(val, option.label ?? option.value ?? "");
    });
    return map;
  }, [options]);

  useEffect(() => {
    const handleClick = (event) => {
      if (!containerRef.current) return;
      if (!containerRef.current.contains(event.target)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, []);

  useEffect(() => {
    if (!open) return;
    const btn = buttonRef.current;
    if (!btn) return;
    const measure = () => {
      const { width } = btn.getBoundingClientRect();
      if (width && Math.abs(width - menuWidth) > 0.5) {
        setMenuWidth(width);
      }
    };
    measure();
    window.addEventListener("resize", measure);
    return () => window.removeEventListener("resize", measure);
  }, [open, menuWidth]);

  const optionOrder = useMemo(() => options.map((option) => option.value), [options]);
  const hasOptions = optionOrder.length > 0;
  const allSelected = hasOptions && optionOrder.every((optionValue) => selectedValues.includes(optionValue));
  const filteredOptions = useMemo(() => {
    if (!showSearch) return options;
    const term = searchTerm.trim().toLowerCase();
    if (!term) return options;
    return options.filter((option) => {
      const label = String(option?.label ?? option?.value ?? "").toLowerCase();
      return label.includes(term);
    });
  }, [options, searchTerm, showSearch]);
  const buildNextList = (nextSet) => {
    const ordered = optionOrder.filter((item) => nextSet.has(item));
    selectedValues.forEach((item) => {
      if (!nextSet.has(item)) return;
      if (optionOrder.includes(item)) return;
      ordered.push(item);
    });
    return ordered;
  };

  const toggleOption = (optionValue) => {
    const nextSet = new Set(selectedValues);
    if (nextSet.has(optionValue)) nextSet.delete(optionValue);
    else nextSet.add(optionValue);
    const nextList = buildNextList(nextSet);
    onChange?.(nextList);
  };

  const clearSelection = () => {
    if (selectedValues.length === 0) return;
    onChange?.([]);
  };
  const handleSelectAll = () => {
    if (!hasOptions || allSelected) return;
    onChange?.(optionOrder.slice());
  };

  const labels = selectedValues.map((val) => labelMap.get(val) || String(val || ""));
  let summary = placeholder;
  if (labels.length === 1) summary = labels[0] || placeholder;
  else if (labels.length === 2) summary = labels.join(", ");
  else if (labels.length > 2) summary = `${labels.length} selected`;

  return (
    <div className="relative" ref={containerRef}>
      <button
        type="button"
        ref={buttonRef}
        className={`flex w-full items-center justify-between gap-2 rounded border border-slate-300 bg-white px-2 py-1 text-left text-xs shadow-sm hover:border-slate-400 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 ${buttonClassName}`}
        onClick={() => setOpen((prev) => !prev)}
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        <span className="truncate text-slate-700">{summary || placeholder}</span>
        <svg
          className={`h-4 w-4 text-slate-500 transition-transform ${open ? "rotate-180" : ""}`}
          viewBox="0 0 20 20"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
        >
          <path d="M6 8l4 4 4-4" />
        </svg>
      </button>

      {open && (
        <div
          className="absolute right-0 z-20 mt-1 rounded-md border border-slate-200 bg-white shadow-lg"
          style={{
            minWidth: menuWidth || undefined,
            width: menuWidth || undefined,
          }}
        >
          {(showSelectAll || showSearch) && (
            <div className="sticky top-0 z-10 border-b border-slate-200 bg-white">
              <div className="flex items-center justify-between px-3 py-1.5 text-xs">
                {showSelectAll ? (
                  <button
                    type="button"
                    onClick={handleSelectAll}
                    className="text-blue-600 hover:underline disabled:cursor-not-allowed disabled:opacity-40"
                    disabled={!hasOptions || allSelected}
                  >
                    Select All
                  </button>
                ) : (
                  <span />
                )}
                <div className="flex items-center gap-3">
                  <button
                    type="button"
                    onClick={clearSelection}
                    className="text-blue-600 hover:underline disabled:cursor-not-allowed disabled:opacity-40"
                    disabled={selectedValues.length === 0}
                  >
                    Clear
                  </button>
                  <button
                    type="button"
                    onClick={() => setOpen(false)}
                    className="text-blue-600 hover:underline"
                  >
                    Close
                  </button>
                </div>
              </div>
              {showSearch && (
                <div className="border-t border-slate-200 px-3 py-2">
                  <input
                    type="text"
                    value={searchTerm}
                    onChange={(event) => setSearchTerm(event.target.value)}
                    placeholder="Search..."
                    className="w-full rounded border border-slate-300 px-2 py-1 text-xs focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                  />
                </div>
              )}
            </div>
          )}
          <div className="max-h-60 overflow-y-auto py-1">
            {filteredOptions.length ? (
              filteredOptions.map((option) => {
                const val = option.value;
                const label = option.label ?? option.value ?? "";
                const checked = selectedValues.includes(val);
                return (
                  <label
                    key={val}
                    className="flex cursor-pointer items-center gap-2 px-3 py-1.5 text-sm text-slate-700 hover:bg-slate-100"
                  >
                    <input
                      type="checkbox"
                      className="h-4 w-4 accent-blue-600"
                      checked={checked}
                      onChange={() => toggleOption(val)}
                    />
                    <span className="truncate">{label || "(Blank)"}</span>
                  </label>
                );
              })
            ) : (
              <div className="px-3 py-2 text-xs text-slate-500">No options available</div>
            )}
          </div>
          {!showSelectAll && !showSearch && (
            <div className="flex items-center justify-between border-t border-slate-200 px-3 py-1.5 text-xs">
              <button
                type="button"
                onClick={clearSelection}
                className="text-blue-600 hover:underline disabled:cursor-not-allowed disabled:opacity-40"
                disabled={selectedValues.length === 0}
              >
                Clear
              </button>
              <button
                type="button"
                onClick={() => setOpen(false)}
                className="text-blue-600 hover:underline"
              >
                Close
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}


